/**
 * Responsibility: Define shared commerce plan, entitlement, and admin-preforge contracts.
 * Scope: Contract normalization and local persistence only; UI orchestration and billing/webhook IO stay elsewhere.
 */

export const COMMERCE_PLAN_IDS = ['free', 'standard', 'pro'] as const
export type CommercePlanId = typeof COMMERCE_PLAN_IDS[number]

export interface CommercePlan {
  id: CommercePlanId
  label: string
  active: boolean
  monthlyPriceUsdCents: number
  managedActionQuota: number
  managedImageQuota: number
  creatorPublishSlots: number
  hostedProxyAccess: boolean
  managedImages: boolean
  creatorConsoleAccess: boolean
  preforgedRealityAccess: boolean
}

export interface EntitlementSnapshot {
  planId: CommercePlanId
  active: boolean
  hostedProxyAccess: boolean
  managedImages: boolean
  creatorConsoleAccess: boolean
  preforgedRealityAccess: boolean
  managedActionQuota: number
  managedImageQuota: number
  creatorPublishSlots: number
  source: 'default' | 'claims' | 'allowlist' | 'admin' | 'gate_off'
}

export type CommerceUsageMode = 'managed' | 'byok' | 'local'
export type ManagedUsageKind = 'action' | 'image'

export interface ManagedUsageEntry {
  id: string
  ts: number
  userUid: string
  userEmail: string
  kind: ManagedUsageKind
  usageMode: CommerceUsageMode
  provider: string
  jobType: string
  inputTokens: number
  outputTokens: number
}

export interface ManagedUsageSummary {
  managedActionQuota: number
  managedImageQuota: number
  managedActionUsed: number
  managedImageUsed: number
  managedActionRemaining: number
  managedImageRemaining: number
  byokActionCount: number
  byokImageCount: number
  localActionCount: number
  localImageCount: number
}

export type CommerceTesterGrantKind = 'plan' | 'marketplace_world' | 'preforged_offer'
export type CommerceTesterGrantState = 'active' | 'revoked'

export interface CommerceTesterGrant {
  kind: CommerceTesterGrantKind
  uid?: string
  email?: string
  displayName?: string
  planId?: CommercePlanId
  artifactId?: string
  offerId?: string
  title?: string
  state: CommerceTesterGrantState
  updatedAtMs?: number
  updatedByUid?: string
  updatedByEmail?: string
}

export type MarketplaceReceiptState = 'owned' | 'refunded'
export type MarketplaceReceiptSource = 'free' | 'included_plan' | 'purchase' | 'admin_grant'

export interface MarketplaceArtifactReceipt {
  artifactId: string
  artifactKind: 'marketplace_world'
  userUid: string
  userEmail: string
  title: string
  state: MarketplaceReceiptState
  source: MarketplaceReceiptSource
  planId: CommercePlanId
  amountPaidCents: number
  currency: string
  acquiredAtMs: number
  updatedAtMs: number
  refundedAtMs?: number
}

export interface MarketplaceAccessDescriptor {
  allowed: boolean
  owned: boolean
  included: boolean
  requiresAcquire: boolean
  reason: 'free' | 'owned' | 'admin_grant' | 'included_plan' | 'paid_locked'
  ctaLabel: string
  statusLabel: string
}

export interface PreforgedOfferAccessDescriptor {
  allowed: boolean
  granted: boolean
  included: boolean
  reason: 'admin_grant' | 'included_plan' | 'locked'
  ctaLabel: string
  statusLabel: string
}

export type PreforgedOfferKind = 'world' | 'reality'
export type PreforgedOfferPriceModel = 'included' | 'paid'
export type PreforgedOfferState = 'draft' | 'published' | 'retired'
export type PreforgeScale = 'small' | 'medium' | 'large'
export type PreforgeOutputMode = 'world-only' | 'full-reality' | 'starter-only' | 'published-offer'
export type PreforgeAiProviderId = 'auto' | 'vertex-managed' | 'gemini' | 'anthropic' | 'openai' | 'groq' | 'ollama'

export interface PreforgeAiSelection {
  providerId: PreforgeAiProviderId
  model: string
}

export type PreforgeSourceWorldRef =
  | { kind: 'liveRun'; saveId: string }
  | { kind: 'savedWorld'; saveId: string }
  | { kind: 'templateWorld'; templateId: string }
  | { kind: 'blankPreset'; presetId: string }

export interface PreforgeDensityTargets {
  atlasNodes: number
  rooms: number
  npcs: number
  companions: number
  items: number
  quests: number
  clocks: number
  events: number
}

export interface PreforgeToneProfile {
  mood: string
  dangerLevel: 'low' | 'medium' | 'high'
  socialFocus: number
  explorationFocus: number
  combatFocus: number
}

export type PreforgeInstancingStrategy = 'shared-persistent' | 'ephemeral-per-user'

export interface PreforgePhysicsProfile {
  gravityConstant: string
  atmosphericMix: string
  entropyDecayRate: string
  viscosityThreshold: string
}

export interface PreforgeSystemProfile {
  maxTesterConcurrency: number
  instancingStrategy: PreforgeInstancingStrategy
  safetyFilterSensitivity: number
}

export interface PreforgeConstraints {
  tokenBudget: number
  timeBudgetMs: number
  forbiddenTopics: string[]
  keepOutRegions: string[]
  minimumContentFloors: Partial<PreforgeDensityTargets>
}

export interface PreforgeRecipe {
  sourceWorld: PreforgeSourceWorldRef
  scale: PreforgeScale
  density: PreforgeDensityTargets
  tone: PreforgeToneProfile
  physics: PreforgePhysicsProfile
  system: PreforgeSystemProfile
  constraints: PreforgeConstraints
  outputMode: PreforgeOutputMode
  aiSelection: PreforgeAiSelection
}

export type PreforgeArtifactMode = 'create' | 'select'
export type PreforgeTextletKind = 'seed' | 'vessel' | 'lore' | 'npc' | 'quest' | 'item' | 'room'

export interface PreforgeTextlet {
  kind: PreforgeTextletKind
  title: string
  body: string
}

export interface PreforgeDraftArtifact {
  mode: PreforgeArtifactMode
  code: string
  title: string
  summary: string
}

export interface PreforgeDraftVessel {
  mode: PreforgeArtifactMode
  code: string
  name: string
  identity: string
  lineage: string
  sex: 'female' | 'male' | 'unspecified'
  bio: string
}

export interface PreforgeDraftTextlets {
  worldLore: string
  openingPitch: string
  npcThreads: string
  questThreads: string
  itemHooks: string
  roomSeeds: string
}

export interface PreforgeDraftAnalysis {
  intentSummary: string
  scopeSummary: string
  keyEntities: string[]
  assumptions: string[]
  ambiguities: string[]
  generationNotes: string[]
}

export interface PreforgeDraft {
  brief: string
  intentSummary: string
  analysis: PreforgeDraftAnalysis
  seed: PreforgeDraftArtifact
  vessel: PreforgeDraftVessel
  textlets: PreforgeDraftTextlets
  generatedAtMs?: number
}

export type PreforgeForgeReportStatus = 'below' | 'met' | 'exceeded'

export interface PreforgeForgeReportMeasure {
  label: string
  requested: number
  actual: number
  delta: number
  status: PreforgeForgeReportStatus
}

export interface PreforgeForgeReport {
  title: string
  document: string
  commercial: string
  requested: PreforgeDensityTargets
  actual: PreforgeDensityTargets
  measures: PreforgeForgeReportMeasure[]
  compliance: string[]
  notes: string[]
  generatedAtMs?: number
}

export interface PreforgedOffer {
  id: string
  kind: PreforgedOfferKind
  versionLabel?: string
  variantOfOfferId?: string
  title: string
  summary: string
  worldManifestId: string
  seedCode?: string
  vesselCode: string
  starterCode: string
  includedPlanId: CommercePlanId
  priceModel: PreforgedOfferPriceModel
  priceCents: number
  currency: string
  publishState: PreforgedOfferState
  forgeRecipe?: PreforgeRecipe
  preforgeDraft?: PreforgeDraft
  forgeReport?: PreforgeForgeReport
  updatedAtMs?: number
  updatedByUid?: string
  updatedByEmail?: string
}

export const LOCAL_COMMERCE_PLANS_KEY = 'am2_commerce_plans_v1'
export const LOCAL_PREFORGED_OFFERS_KEY = 'am2_preforged_offers_v1'
export const LOCAL_ENTITLEMENT_SNAPSHOT_KEY = 'am2_entitlement_snapshot_v1'
export const LOCAL_MANAGED_USAGE_LEDGER_KEY = 'am2_managed_usage_ledger_v1'
export const LOCAL_MARKETPLACE_RECEIPTS_KEY = 'am2_marketplace_receipts_v1'
export const LOCAL_TESTER_GRANTS_KEY = 'am2_tester_grants_v1'

export const DEFAULT_COMMERCE_PLANS: CommercePlan[] = [
  {
    id: 'free',
    label: 'Free',
    active: true,
    monthlyPriceUsdCents: 0,
    managedActionQuota: 0,
    managedImageQuota: 0,
    creatorPublishSlots: 1,
    hostedProxyAccess: false,
    managedImages: false,
    creatorConsoleAccess: false,
    preforgedRealityAccess: false,
  },
  {
    id: 'standard',
    label: 'Standard',
    active: true,
    monthlyPriceUsdCents: 499,
    managedActionQuota: 2000,
    managedImageQuota: 60,
    creatorPublishSlots: 5,
    hostedProxyAccess: true,
    managedImages: true,
    creatorConsoleAccess: true,
    preforgedRealityAccess: false,
  },
  {
    id: 'pro',
    label: 'Pro',
    active: true,
    monthlyPriceUsdCents: 1499,
    managedActionQuota: 10000,
    managedImageQuota: 240,
    creatorPublishSlots: 999,
    hostedProxyAccess: true,
    managedImages: true,
    creatorConsoleAccess: true,
    preforgedRealityAccess: true,
  },
]

function safeObject(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
}

function shortText(value: unknown, fallback = ''): string {
  return String(value ?? '').trim() || fallback
}

function readInt(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : fallback
}

function readMoneyCents(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : fallback
}

function readTimestamp(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : fallback
}

function splitList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(entry => shortText(entry)).filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(/[\n,;|]+/)
      .map(entry => entry.trim())
      .filter(Boolean)
  }
  return []
}

function readBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return /^(true|1|yes|on)$/i.test(value)
  return fallback
}

const PREFORGE_SCALE_DEFAULTS: Record<PreforgeScale, PreforgeDensityTargets> = {
  small: {
    atlasNodes: 8,
    rooms: 6,
    npcs: 4,
    companions: 1,
    items: 8,
    quests: 2,
    clocks: 2,
    events: 3,
  },
  medium: {
    atlasNodes: 18,
    rooms: 12,
    npcs: 8,
    companions: 2,
    items: 16,
    quests: 4,
    clocks: 4,
    events: 6,
  },
  large: {
    atlasNodes: 36,
    rooms: 24,
    npcs: 16,
    companions: 4,
    items: 32,
    quests: 8,
    clocks: 8,
    events: 12,
  },
}

const PREFORGE_PHYSICS_DEFAULTS: Record<PreforgeScale, PreforgePhysicsProfile> = {
  small: {
    gravityConstant: '0.98g',
    atmosphericMix: 'nitrogen-oxygen',
    entropyDecayRate: '0.02',
    viscosityThreshold: 'medium',
  },
  medium: {
    gravityConstant: '1.00g',
    atmosphericMix: 'mixed breathable',
    entropyDecayRate: '0.05',
    viscosityThreshold: 'medium-high',
  },
  large: {
    gravityConstant: '1.15g',
    atmosphericMix: 'dense mixed',
    entropyDecayRate: '0.08',
    viscosityThreshold: 'high',
  },
}

const PREFORGE_SYSTEM_DEFAULTS: Record<PreforgeScale, PreforgeSystemProfile> = {
  small: {
    maxTesterConcurrency: 1,
    instancingStrategy: 'ephemeral-per-user',
    safetyFilterSensitivity: 8,
  },
  medium: {
    maxTesterConcurrency: 2,
    instancingStrategy: 'shared-persistent',
    safetyFilterSensitivity: 7,
  },
  large: {
    maxTesterConcurrency: 4,
    instancingStrategy: 'shared-persistent',
    safetyFilterSensitivity: 6,
  },
}

function normalizePreforgeScale(value: unknown): PreforgeScale {
  const raw = shortText(value).toLowerCase()
  if (raw === 'small' || raw === 'large') return raw
  return 'medium'
}

function normalizePreforgeOutputMode(value: unknown): PreforgeOutputMode {
  const raw = shortText(value).toLowerCase()
  if (raw === 'world-only' || raw === 'starter-only' || raw === 'published-offer') return raw
  return 'full-reality'
}

function normalizePreforgeAiProviderId(value: unknown): PreforgeAiProviderId {
  const raw = shortText(value).toLowerCase()
  if (raw === 'vertex-managed' || raw === 'gemini' || raw === 'anthropic' || raw === 'openai' || raw === 'groq' || raw === 'ollama') {
    return raw
  }
  return 'auto'
}

function normalizePreforgeAiSelection(raw: unknown, fallback: PreforgeAiSelection): PreforgeAiSelection {
  const entry = safeObject(raw)
  return {
    providerId: normalizePreforgeAiProviderId(entry.providerId ?? entry.provider ?? fallback.providerId),
    model: shortText(entry.model ?? fallback.model),
  }
}

function normalizePreforgeSourceWorldRef(raw: unknown): PreforgeSourceWorldRef | null {
  const entry = safeObject(raw)
  const kind = shortText(entry.kind).toLowerCase()
  if (kind === 'liverun') {
    const saveId = shortText(entry.saveId)
    return saveId ? { kind: 'liveRun', saveId } : null
  }
  if (kind === 'savedworld') {
    const saveId = shortText(entry.saveId)
    return saveId ? { kind: 'savedWorld', saveId } : null
  }
  if (kind === 'templateworld') {
    const templateId = shortText(entry.templateId || entry.saveId || entry.worldManifestId)
    return templateId ? { kind: 'templateWorld', templateId } : null
  }
  if (kind === 'blankpreset') {
    const presetId = shortText(entry.presetId || entry.saveId || entry.templateId || 'custom')
    return presetId ? { kind: 'blankPreset', presetId } : null
  }
  const fallbackId = shortText(entry.saveId || entry.templateId || entry.presetId)
  if (fallbackId) {
    return { kind: 'savedWorld', saveId: fallbackId }
  }
  return null
}

function normalizePreforgeDensityTargets(raw: unknown, fallback: PreforgeDensityTargets): PreforgeDensityTargets {
  const entry = safeObject(raw)
  return {
    atlasNodes: readInt(entry.atlasNodes, fallback.atlasNodes),
    rooms: readInt(entry.rooms, fallback.rooms),
    npcs: readInt(entry.npcs, fallback.npcs),
    companions: readInt(entry.companions, fallback.companions),
    items: readInt(entry.items, fallback.items),
    quests: readInt(entry.quests, fallback.quests),
    clocks: readInt(entry.clocks, fallback.clocks),
    events: readInt(entry.events, fallback.events),
  }
}

function normalizePreforgeToneProfile(raw: unknown, fallback: PreforgeToneProfile): PreforgeToneProfile {
  const entry = safeObject(raw)
  const dangerLevel = shortText(entry.dangerLevel).toLowerCase()
  return {
    mood: shortText(entry.mood, fallback.mood),
    dangerLevel: dangerLevel === 'low' || dangerLevel === 'high' ? dangerLevel : fallback.dangerLevel,
    socialFocus: readInt(entry.socialFocus, fallback.socialFocus),
    explorationFocus: readInt(entry.explorationFocus, fallback.explorationFocus),
    combatFocus: readInt(entry.combatFocus, fallback.combatFocus),
  }
}

function normalizePreforgePhysicsProfile(raw: unknown, fallback: PreforgePhysicsProfile): PreforgePhysicsProfile {
  const entry = safeObject(raw)
  return {
    gravityConstant: shortText(entry.gravityConstant, fallback.gravityConstant),
    atmosphericMix: shortText(entry.atmosphericMix, fallback.atmosphericMix),
    entropyDecayRate: shortText(entry.entropyDecayRate, fallback.entropyDecayRate),
    viscosityThreshold: shortText(entry.viscosityThreshold, fallback.viscosityThreshold),
  }
}

function normalizePreforgeSystemProfile(raw: unknown, fallback: PreforgeSystemProfile): PreforgeSystemProfile {
  const entry = safeObject(raw)
  const instancingStrategy = shortText(entry.instancingStrategy).toLowerCase()
  return {
    maxTesterConcurrency: readInt(entry.maxTesterConcurrency, fallback.maxTesterConcurrency),
    instancingStrategy: instancingStrategy === 'ephemeral-per-user' || instancingStrategy === 'shared-persistent'
      ? instancingStrategy
      : fallback.instancingStrategy,
    safetyFilterSensitivity: readInt(entry.safetyFilterSensitivity, fallback.safetyFilterSensitivity),
  }
}

function normalizePreforgeConstraints(
  raw: unknown,
  densityFallback: PreforgeDensityTargets,
): PreforgeConstraints {
  const entry = safeObject(raw)
  const fallbackMinimums = {
    atlasNodes: Math.max(1, Math.floor(densityFallback.atlasNodes * 0.75)),
    rooms: Math.max(1, Math.floor(densityFallback.rooms * 0.75)),
    npcs: Math.max(1, Math.floor(densityFallback.npcs * 0.75)),
    companions: Math.max(0, Math.floor(densityFallback.companions * 0.5)),
    items: Math.max(1, Math.floor(densityFallback.items * 0.75)),
    quests: Math.max(1, Math.floor(densityFallback.quests * 0.75)),
    clocks: Math.max(0, Math.floor(densityFallback.clocks * 0.5)),
    events: Math.max(0, Math.floor(densityFallback.events * 0.5)),
  }
  return {
    tokenBudget: readInt(entry.tokenBudget, 0),
    timeBudgetMs: readInt(entry.timeBudgetMs, 0),
    forbiddenTopics: splitList(entry.forbiddenTopics),
    keepOutRegions: splitList(entry.keepOutRegions),
    minimumContentFloors: normalizePreforgeDensityTargets(entry.minimumContentFloors, fallbackMinimums),
  }
}

export function buildPreforgeRecipeDefaults(
  sourceWorld: PreforgeSourceWorldRef,
  scale: PreforgeScale = 'medium',
  outputMode: PreforgeOutputMode = 'full-reality',
  aiSelection: Partial<PreforgeAiSelection> = {},
): PreforgeRecipe {
  const density = {
    ...PREFORGE_SCALE_DEFAULTS[scale],
  }
  const toneByScale: Record<PreforgeScale, PreforgeToneProfile> = {
    small: { mood: 'focused and intimate', dangerLevel: 'medium', socialFocus: 45, explorationFocus: 35, combatFocus: 20 },
    medium: { mood: 'balanced and lively', dangerLevel: 'medium', socialFocus: 35, explorationFocus: 40, combatFocus: 25 },
    large: { mood: 'dense and theatrical', dangerLevel: 'high', socialFocus: 30, explorationFocus: 38, combatFocus: 32 },
  }
  const physics = PREFORGE_PHYSICS_DEFAULTS[scale]
  const system = PREFORGE_SYSTEM_DEFAULTS[scale]
  const tone = toneByScale[scale]
  const constraints: PreforgeConstraints = {
    tokenBudget: scale === 'large' ? 20000 : scale === 'small' ? 9000 : 14000,
    timeBudgetMs: scale === 'large' ? 180000 : scale === 'small' ? 60000 : 120000,
    forbiddenTopics: [],
    keepOutRegions: [],
    minimumContentFloors: {
      atlasNodes: Math.max(1, Math.floor(density.atlasNodes * 0.75)),
      rooms: Math.max(1, Math.floor(density.rooms * 0.75)),
      npcs: Math.max(1, Math.floor(density.npcs * 0.75)),
      companions: Math.max(0, Math.floor(density.companions * 0.5)),
      items: Math.max(1, Math.floor(density.items * 0.75)),
      quests: Math.max(1, Math.floor(density.quests * 0.75)),
      clocks: Math.max(0, Math.floor(density.clocks * 0.5)),
      events: Math.max(0, Math.floor(density.events * 0.5)),
    },
  }
  return {
    sourceWorld,
    scale,
    density,
    tone,
    physics,
    system,
    constraints,
    outputMode,
    aiSelection: {
      providerId: normalizePreforgeAiProviderId(aiSelection.providerId),
      model: shortText(aiSelection.model),
    },
  }
}

function normalizePreforgeRecipe(raw: unknown): PreforgeRecipe | null {
  const entry = safeObject(raw)
  const sourceShape = safeObject(entry.forgeRecipe ?? entry)
  const sourceWorld = normalizePreforgeSourceWorldRef(sourceShape.sourceWorld ?? {
    kind: sourceShape.sourceWorldKind,
    saveId: sourceShape.sourceWorldId,
    templateId: sourceShape.sourceWorldId,
    presetId: sourceShape.sourceWorldId,
  }) ?? normalizePreforgeSourceWorldRef({ kind: 'blankPreset', presetId: 'custom' })
  if (!sourceWorld) return null
  const scale = normalizePreforgeScale(sourceShape.scale)
  const density = normalizePreforgeDensityTargets(sourceShape.density, PREFORGE_SCALE_DEFAULTS[scale])
  const physics = normalizePreforgePhysicsProfile(sourceShape.physics, PREFORGE_PHYSICS_DEFAULTS[scale])
  const system = normalizePreforgeSystemProfile(sourceShape.system, PREFORGE_SYSTEM_DEFAULTS[scale])
  return {
    sourceWorld,
    scale,
    density,
    tone: normalizePreforgeToneProfile(sourceShape.tone, {
      mood: 'balanced and lively',
      dangerLevel: 'medium',
      socialFocus: 35,
      explorationFocus: 40,
      combatFocus: 25,
    }),
    physics,
    system,
    constraints: normalizePreforgeConstraints(sourceShape.constraints, density),
    outputMode: normalizePreforgeOutputMode(sourceShape.outputMode),
    aiSelection: normalizePreforgeAiSelection(sourceShape.aiSelection ?? sourceShape.ai, {
      providerId: normalizePreforgeAiProviderId(sourceShape.aiProviderId ?? sourceShape.providerId),
      model: shortText(sourceShape.aiModel ?? sourceShape.model),
    }),
  }
}

function normalizePreforgeDraft(raw: unknown): PreforgeDraft | null {
  const entry = safeObject(raw)
  const source = safeObject(entry.preforgeDraft ?? entry.draft ?? entry)
  const brief = shortText(source.brief ?? entry.brief)
  const analysisSource = safeObject(source.analysis ?? entry.analysis)
  const seed = safeObject(source.seed ?? entry.seed)
  const vessel = safeObject(source.vessel ?? entry.vessel)
  const textletsSource = safeObject(source.textlets)
  const intentSummary = shortText(
    source.intentSummary
      ?? analysisSource.intentSummary
      ?? entry.intentSummary
      ?? entry.analysisIntentSummary,
  )
  const analysis: PreforgeDraftAnalysis = {
    intentSummary,
    scopeSummary: shortText(analysisSource.scopeSummary ?? entry.scopeSummary ?? entry.analysisScopeSummary),
    keyEntities: splitList(analysisSource.keyEntities ?? entry.keyEntities ?? entry.analysisKeyEntities),
    assumptions: splitList(analysisSource.assumptions ?? entry.assumptions ?? entry.analysisAssumptions),
    ambiguities: splitList(analysisSource.ambiguities ?? entry.ambiguities ?? entry.analysisAmbiguities),
    generationNotes: splitList(analysisSource.generationNotes ?? entry.generationNotes ?? entry.analysisGenerationNotes),
  }
  const normalized: PreforgeDraft = {
    brief,
    intentSummary,
    analysis,
    seed: {
      mode: shortText(seed.mode, shortText(entry.seedMode, 'create')).toLowerCase() === 'select' ? 'select' : 'create',
      code: shortText(seed.code ?? entry.seedCode),
      title: shortText(seed.title ?? entry.seedTitle),
      summary: shortText(seed.summary ?? entry.seedSummary),
    },
    vessel: {
      mode: shortText(vessel.mode, shortText(entry.vesselMode, 'create')).toLowerCase() === 'select' ? 'select' : 'create',
      code: shortText(vessel.code ?? entry.vesselCode),
      name: shortText(vessel.name ?? entry.vesselName),
      identity: shortText(vessel.identity ?? entry.vesselIdentity),
      lineage: shortText(vessel.lineage ?? entry.vesselLineage),
      sex: ['female', 'male', 'unspecified'].includes(shortText(vessel.sex ?? entry.vesselSex).toLowerCase())
        ? shortText(vessel.sex ?? entry.vesselSex).toLowerCase() as PreforgeDraftVessel['sex']
        : 'unspecified',
      bio: shortText(vessel.bio ?? entry.vesselBio),
    },
    textlets: {
      worldLore: shortText(textletsSource.worldLore ?? entry.textletWorldLore),
      openingPitch: shortText(textletsSource.openingPitch ?? entry.textletOpeningPitch),
      npcThreads: shortText(textletsSource.npcThreads ?? entry.textletNpcThreads),
      questThreads: shortText(textletsSource.questThreads ?? entry.textletQuestThreads),
      itemHooks: shortText(textletsSource.itemHooks ?? entry.textletItemHooks),
      roomSeeds: shortText(textletsSource.roomSeeds ?? entry.textletRoomSeeds),
    },
    ...(Number.isFinite(source.generatedAtMs ?? entry.generatedAtMs) ? { generatedAtMs: Number(source.generatedAtMs ?? entry.generatedAtMs) } : {}),
  }
  if (!normalized.brief && !normalized.intentSummary && !normalized.seed.code && !normalized.vessel.code && !Object.values(normalized.textlets).some(Boolean) && !normalized.analysis.keyEntities.length && !normalized.analysis.assumptions.length && !normalized.analysis.ambiguities.length) {
    return null
  }
  return normalized
}

function normalizePreforgeForgeReportMeasure(raw: unknown): PreforgeForgeReportMeasure | null {
  const entry = safeObject(raw)
  const label = shortText(entry.label)
  if (!label) return null
  const requested = readInt(entry.requested)
  const actual = readInt(entry.actual)
  const delta = Number.isFinite(Number(entry.delta)) ? Number(entry.delta) : actual - requested
  const statusRaw = shortText(entry.status).toLowerCase()
  const status: PreforgeForgeReportStatus = statusRaw === 'below'
    ? 'below'
    : statusRaw === 'exceeded'
      ? 'exceeded'
      : 'met'
  return { label, requested, actual, delta, status }
}

function normalizePreforgeForgeReport(raw: unknown): PreforgeForgeReport | null {
  const entry = safeObject(raw)
  const source = safeObject(entry.forgeReport ?? entry.report ?? entry)
  const title = shortText(source.title)
  const document = shortText(source.document ?? source.body)
  const commercial = shortText(source.commercial ?? source.summary)
  const requested = normalizePreforgeDensityTargets(source.requested, {
    atlasNodes: 0,
    rooms: 0,
    npcs: 0,
    companions: 0,
    items: 0,
    quests: 0,
    clocks: 0,
    events: 0,
  })
  const actual = normalizePreforgeDensityTargets(source.actual, requested)
  const measures = Array.isArray(source.measures)
    ? source.measures.map(normalizePreforgeForgeReportMeasure).filter((item): item is PreforgeForgeReportMeasure => !!item)
    : []
  const compliance = splitList(source.compliance)
  const notes = splitList(source.notes)
  if (!title && !document && !commercial && !measures.length && !compliance.length && !notes.length) return null
  return {
    title: title || 'Forge Report',
    document,
    commercial,
    requested,
    actual,
    measures,
    compliance,
    notes,
    ...(Number.isFinite(source.generatedAtMs ?? entry.generatedAtMs) ? { generatedAtMs: Number(source.generatedAtMs ?? entry.generatedAtMs) } : {}),
  }
}

export function normalizeCommercePlanId(value: unknown): CommercePlanId {
  const raw = shortText(value).toLowerCase()
  if (raw === 'pro' || raw === 'premium' || raw === 'paid' || raw === 'blaze') return 'pro'
  if (raw === 'standard' || raw === 'plus' || raw === 'basic') return 'standard'
  return 'free'
}

export function normalizeCommercePlan(raw: unknown, fallbackId?: CommercePlanId): CommercePlan {
  const entry = safeObject(raw)
  const planId = normalizeCommercePlanId(entry.id ?? fallbackId ?? 'free')
  const defaults = DEFAULT_COMMERCE_PLANS.find(plan => plan.id === planId) ?? DEFAULT_COMMERCE_PLANS[0]
  return {
    id: planId,
    label: shortText(entry.label, defaults.label),
    active: readBool(entry.active, defaults.active),
    monthlyPriceUsdCents: readMoneyCents(entry.monthlyPriceUsdCents, defaults.monthlyPriceUsdCents),
    managedActionQuota: readInt(entry.managedActionQuota, defaults.managedActionQuota),
    managedImageQuota: readInt(entry.managedImageQuota, defaults.managedImageQuota),
    creatorPublishSlots: readInt(entry.creatorPublishSlots, defaults.creatorPublishSlots),
    hostedProxyAccess: readBool(entry.hostedProxyAccess, defaults.hostedProxyAccess),
    managedImages: readBool(entry.managedImages, defaults.managedImages),
    creatorConsoleAccess: readBool(entry.creatorConsoleAccess, defaults.creatorConsoleAccess),
    preforgedRealityAccess: readBool(entry.preforgedRealityAccess, defaults.preforgedRealityAccess),
  }
}

export function normalizeCommercePlans(raw: unknown): CommercePlan[] {
  const source = Array.isArray(raw) ? raw : []
  const byId = new Map<CommercePlanId, CommercePlan>()
  for (const id of COMMERCE_PLAN_IDS) {
    byId.set(id, normalizeCommercePlan({}, id))
  }
  for (const candidate of source) {
    const normalized = normalizeCommercePlan(candidate)
    byId.set(normalized.id, normalized)
  }
  return COMMERCE_PLAN_IDS.map(id => byId.get(id)!)
}

export function planById(plans: CommercePlan[], planId: unknown): CommercePlan {
  const normalizedId = normalizeCommercePlanId(planId)
  return normalizeCommercePlans(plans).find(plan => plan.id === normalizedId) ?? normalizeCommercePlan({}, normalizedId)
}

export function buildEntitlementSnapshot(raw: unknown, plans: CommercePlan[] = DEFAULT_COMMERCE_PLANS): EntitlementSnapshot {
  const entry = safeObject(raw)
  const amEntitlements = safeObject(entry.am_entitlements)
  const entitlements = safeObject(entry.entitlements)
  const plan = planById(plans, entry.planId ?? entry.plan ?? entry.tier ?? amEntitlements.planId)
  const active = readBool(entry.active ?? entry.subscriptionActive ?? entry.am_subscriber, plan.id !== 'free')
  return {
    planId: plan.id,
    active,
    hostedProxyAccess: readBool(entry.hostedProxyAccess ?? entitlements.hosted_proxy ?? amEntitlements.hostedProxyAccess, active && plan.hostedProxyAccess),
    managedImages: readBool(entry.managedImages ?? amEntitlements.managedImages, plan.managedImages),
    creatorConsoleAccess: readBool(entry.creatorConsoleAccess ?? amEntitlements.creatorConsoleAccess, plan.creatorConsoleAccess),
    preforgedRealityAccess: readBool(entry.preforgedRealityAccess ?? amEntitlements.preforgedRealityAccess, plan.preforgedRealityAccess),
    managedActionQuota: readInt(entry.managedActionQuota ?? amEntitlements.managedActionQuota, plan.managedActionQuota),
    managedImageQuota: readInt(entry.managedImageQuota ?? amEntitlements.managedImageQuota, plan.managedImageQuota),
    creatorPublishSlots: readInt(entry.creatorPublishSlots ?? amEntitlements.creatorPublishSlots, plan.creatorPublishSlots),
    source: shortText(entry.source, 'default') as EntitlementSnapshot['source'],
  }
}

export function normalizeEntitlementSnapshot(raw: unknown, plans: CommercePlan[] = DEFAULT_COMMERCE_PLANS): EntitlementSnapshot {
  return buildEntitlementSnapshot(raw, plans)
}

function usageMode(value: unknown): CommerceUsageMode {
  const raw = shortText(value).toLowerCase()
  if (raw === 'managed') return 'managed'
  if (raw === 'byok') return 'byok'
  return 'local'
}

export function normalizeManagedUsageEntry(raw: unknown): ManagedUsageEntry | null {
  const entry = safeObject(raw)
  const id = shortText(entry.id)
  const ts = readTimestamp(entry.ts)
  const userUid = shortText(entry.userUid)
  const kind: ManagedUsageKind = shortText(entry.kind).toLowerCase() === 'image' ? 'image' : 'action'
  if (!id || !ts || !userUid) return null
  return {
    id,
    ts,
    userUid,
    userEmail: shortText(entry.userEmail).toLowerCase(),
    kind,
    usageMode: usageMode(entry.usageMode),
    provider: shortText(entry.provider, 'unknown'),
    jobType: shortText(entry.jobType, kind === 'image' ? 'image' : 'logic_forge'),
    inputTokens: readInt(entry.inputTokens),
    outputTokens: readInt(entry.outputTokens),
  }
}

export function normalizeManagedUsageLedger(raw: unknown): ManagedUsageEntry[] {
  const source = Array.isArray(raw) ? raw : []
  const entries = source
    .map(normalizeManagedUsageEntry)
    .filter((entry): entry is ManagedUsageEntry => !!entry)
  return entries.sort((a, b) => b.ts - a.ts)
}

export function summarizeManagedUsage(entries: ManagedUsageEntry[], entitlement: EntitlementSnapshot): ManagedUsageSummary {
  const summary: ManagedUsageSummary = {
    managedActionQuota: entitlement.managedActionQuota,
    managedImageQuota: entitlement.managedImageQuota,
    managedActionUsed: 0,
    managedImageUsed: 0,
    managedActionRemaining: entitlement.managedActionQuota,
    managedImageRemaining: entitlement.managedImageQuota,
    byokActionCount: 0,
    byokImageCount: 0,
    localActionCount: 0,
    localImageCount: 0,
  }
  for (const entry of entries) {
    if (entry.kind === 'image') {
      if (entry.usageMode === 'managed') summary.managedImageUsed += 1
      else if (entry.usageMode === 'byok') summary.byokImageCount += 1
      else summary.localImageCount += 1
      continue
    }
    if (entry.usageMode === 'managed') summary.managedActionUsed += 1
    else if (entry.usageMode === 'byok') summary.byokActionCount += 1
    else summary.localActionCount += 1
  }
  summary.managedActionRemaining = Math.max(0, summary.managedActionQuota - summary.managedActionUsed)
  summary.managedImageRemaining = Math.max(0, summary.managedImageQuota - summary.managedImageUsed)
  return summary
}

function normalizeGrantKind(value: unknown): CommerceTesterGrantKind {
  const raw = shortText(value).toLowerCase()
  if (raw === 'marketplace_world' || raw === 'world' || raw === 'artifact') return 'marketplace_world'
  if (raw === 'preforged_offer' || raw === 'offer' || raw === 'preforged') return 'preforged_offer'
  return 'plan'
}

function normalizeGrantState(value: unknown): CommerceTesterGrantState {
  return shortText(value).toLowerCase() === 'revoked' ? 'revoked' : 'active'
}

function identityKey(uid: unknown, email: unknown): string {
  const normalizedEmail = shortText(email).toLowerCase()
  if (normalizedEmail) return `email:${normalizedEmail}`
  const normalizedUid = shortText(uid)
  return normalizedUid ? `uid:${normalizedUid}` : ''
}

function grantTargetKey(grant: Pick<CommerceTesterGrant, 'kind' | 'planId' | 'artifactId' | 'offerId'>): string {
  if (grant.kind === 'marketplace_world') return `world:${shortText(grant.artifactId)}`
  if (grant.kind === 'preforged_offer') return `offer:${shortText(grant.offerId)}`
  return `plan:${normalizeCommercePlanId(grant.planId)}`
}

export function testerGrantKey(grant: Pick<CommerceTesterGrant, 'uid' | 'email' | 'kind' | 'planId' | 'artifactId' | 'offerId'>): string {
  const who = identityKey(grant.uid, grant.email)
  if (!who) return ''
  const target = grantTargetKey(grant)
  return target ? `${who}::${grant.kind}::${target}` : ''
}

export function normalizeCommerceTesterGrant(raw: unknown): CommerceTesterGrant | null {
  const entry = safeObject(raw)
  const kind = normalizeGrantKind(entry.kind)
  const uid = shortText(entry.uid)
  const email = shortText(entry.email).toLowerCase()
  if (!uid && !email) return null
  const planId = kind === 'plan' ? normalizeCommercePlanId(entry.planId) : undefined
  const artifactId = kind === 'marketplace_world' ? shortText(entry.artifactId ?? entry.worldManifestId) : undefined
  const offerId = kind === 'preforged_offer' ? shortText(entry.offerId) : undefined
  if (kind === 'marketplace_world' && !artifactId) return null
  if (kind === 'preforged_offer' && !offerId) return null
  const grant: CommerceTesterGrant = {
    kind,
    ...(uid ? { uid } : {}),
    ...(email ? { email } : {}),
    ...(shortText(entry.displayName) ? { displayName: shortText(entry.displayName) } : {}),
    ...(planId ? { planId } : {}),
    ...(artifactId ? { artifactId } : {}),
    ...(offerId ? { offerId } : {}),
    ...(shortText(entry.title) ? { title: shortText(entry.title) } : {}),
    state: normalizeGrantState(entry.state),
    ...(readTimestamp(entry.updatedAtMs) ? { updatedAtMs: readTimestamp(entry.updatedAtMs) } : {}),
    ...(shortText(entry.updatedByUid) ? { updatedByUid: shortText(entry.updatedByUid) } : {}),
    ...(shortText(entry.updatedByEmail) ? { updatedByEmail: shortText(entry.updatedByEmail).toLowerCase() } : {}),
  }
  return testerGrantKey(grant) ? grant : null
}

export function normalizeCommerceTesterGrants(raw: unknown): CommerceTesterGrant[] {
  const source = Array.isArray(raw) ? raw : []
  const deduped = new Map<string, CommerceTesterGrant>()
  for (const candidate of source) {
    const grant = normalizeCommerceTesterGrant(candidate)
    if (!grant) continue
    const key = testerGrantKey(grant)
    if (!key) continue
    deduped.set(key, grant)
  }
  return [...deduped.values()].sort((a, b) => {
    const timeDelta = readTimestamp(b.updatedAtMs) - readTimestamp(a.updatedAtMs)
    if (timeDelta) return timeDelta
    return testerGrantKey(a).localeCompare(testerGrantKey(b))
  })
}

export function grantMatchesIdentity(
  grant: Pick<CommerceTesterGrant, 'uid' | 'email'>,
  identity: { uid?: unknown; email?: unknown } | null | undefined,
): boolean {
  if (!identity) return false
  const grantEmail = shortText(grant.email).toLowerCase()
  const identityEmail = shortText(identity.email).toLowerCase()
  if (grantEmail && identityEmail && grantEmail === identityEmail) return true
  const grantUid = shortText(grant.uid)
  const identityUid = shortText(identity.uid)
  return !!grantUid && !!identityUid && grantUid === identityUid
}

export function commercePlanSatisfies(planId: CommercePlanId, requiredPlanId: CommercePlanId): boolean {
  const rank = { free: 0, standard: 1, pro: 2 }
  return rank[planId] >= rank[requiredPlanId]
}

export function normalizeMarketplaceArtifactReceipt(raw: unknown): MarketplaceArtifactReceipt | null {
  const entry = safeObject(raw)
  const artifactId = shortText(entry.artifactId)
  const userUid = shortText(entry.userUid)
  if (!artifactId || !userUid) return null
  const state: MarketplaceReceiptState = shortText(entry.state).toLowerCase() === 'refunded' ? 'refunded' : 'owned'
  const sourceRaw = shortText(entry.source).toLowerCase()
  const source: MarketplaceReceiptSource = sourceRaw === 'purchase'
    ? 'purchase'
    : sourceRaw === 'included_plan'
      ? 'included_plan'
      : sourceRaw === 'admin_grant'
        ? 'admin_grant'
        : 'free'
  return {
    artifactId,
    artifactKind: 'marketplace_world',
    userUid,
    userEmail: shortText(entry.userEmail).toLowerCase(),
    title: shortText(entry.title, artifactId),
    state,
    source,
    planId: normalizeCommercePlanId(entry.planId),
    amountPaidCents: readMoneyCents(entry.amountPaidCents),
    currency: shortText(entry.currency, 'USD').toUpperCase(),
    acquiredAtMs: readTimestamp(entry.acquiredAtMs, Date.now()),
    updatedAtMs: readTimestamp(entry.updatedAtMs, Date.now()),
    ...(readTimestamp(entry.refundedAtMs) ? { refundedAtMs: readTimestamp(entry.refundedAtMs) } : {}),
  }
}

export function normalizeMarketplaceArtifactReceipts(raw: unknown): MarketplaceArtifactReceipt[] {
  const source = Array.isArray(raw) ? raw : []
  const deduped = new Map<string, MarketplaceArtifactReceipt>()
  for (const candidate of source) {
    const receipt = normalizeMarketplaceArtifactReceipt(candidate)
    if (!receipt) continue
    deduped.set(`${receipt.userUid}::${receipt.artifactId}`, receipt)
  }
  return [...deduped.values()].sort((a, b) => b.updatedAtMs - a.updatedAtMs)
}

export function receiptOwnsMarketplaceArtifact(
  receipts: MarketplaceArtifactReceipt[],
  artifactId: string,
): boolean {
  return receipts.some(receipt => receipt.artifactId === artifactId && receipt.state === 'owned')
}

export function describeMarketplaceAccess(
  entry: {
    id: string
    pricing?: { model?: string; entitlementTier?: string }
  },
  entitlement: EntitlementSnapshot,
  receipts: MarketplaceArtifactReceipt[],
): MarketplaceAccessDescriptor {
  const pricingModel = shortText(entry?.pricing?.model).toLowerCase() === 'paid' ? 'paid' : 'free'
  if (pricingModel === 'free') {
    return {
      allowed: true,
      owned: false,
      included: false,
      requiresAcquire: false,
      reason: 'free',
      ctaLabel: 'IMPORT WORLD',
      statusLabel: 'OPEN TO TESTERS',
    }
  }
  const receipt = receipts.find(candidate => candidate.artifactId === entry.id && candidate.state === 'owned')
  if (receipt) {
    const granted = receipt.source === 'admin_grant'
    return {
      allowed: true,
      owned: true,
      included: false,
      requiresAcquire: false,
      reason: granted ? 'admin_grant' : 'owned',
      ctaLabel: granted ? 'IMPORT GRANTED WORLD' : 'IMPORT OWNED WORLD',
      statusLabel: granted ? 'GRANTED FOR THIS TEST' : 'OWNED',
    }
  }
  const requiredPlanId = normalizeCommercePlanId(entry?.pricing?.entitlementTier)
  if (entitlement.active && commercePlanSatisfies(entitlement.planId, requiredPlanId)) {
    return {
      allowed: true,
      owned: false,
      included: true,
      requiresAcquire: false,
      reason: 'included_plan',
      ctaLabel: 'IMPORT INCLUDED WORLD',
      statusLabel: 'INCLUDED IN YOUR TEST PLAN',
    }
  }
  return {
    allowed: false,
    owned: false,
    included: false,
    requiresAcquire: true,
    reason: 'paid_locked',
    ctaLabel: 'TEST ACCESS REQUIRED',
    statusLabel: `ASK AN ADMIN FOR ${requiredPlanId.toUpperCase()} ACCESS`,
  }
}

export function describePreforgedOfferAccess(
  offer: Pick<PreforgedOffer, 'includedPlanId'> & { id: string },
  entitlement: EntitlementSnapshot,
  grants: CommerceTesterGrant[],
): PreforgedOfferAccessDescriptor {
  const granted = grants.some(grant =>
    grant.kind === 'preforged_offer' &&
    grant.state === 'active' &&
    shortText(grant.offerId) === offer.id,
  )
  if (granted) {
    return {
      allowed: true,
      granted: true,
      included: false,
      reason: 'admin_grant',
      ctaLabel: 'START GRANTED REALITY',
      statusLabel: 'GRANTED FOR THIS TEST',
    }
  }
  if (entitlement.active && commercePlanSatisfies(entitlement.planId, offer.includedPlanId)) {
    return {
      allowed: true,
      granted: false,
      included: true,
      reason: 'included_plan',
      ctaLabel: 'START INCLUDED REALITY',
      statusLabel: 'INCLUDED IN YOUR TEST PLAN',
    }
  }
  return {
    allowed: false,
    granted: false,
    included: false,
    reason: 'locked',
    ctaLabel: 'TEST ACCESS REQUIRED',
    statusLabel: `ASK AN ADMIN FOR ${offer.includedPlanId.toUpperCase()} ACCESS`,
  }
}

export function normalizePreforgedOffer(raw: unknown): PreforgedOffer | null {
  const entry = safeObject(raw)
  const id = shortText(entry.id).toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)
  if (!id) return null
  const kind: PreforgedOfferKind = shortText(entry.kind).toLowerCase() === 'reality' ? 'reality' : 'world'
  const publishState: PreforgedOfferState = shortText(entry.publishState).toLowerCase() === 'retired'
    ? 'retired'
    : shortText(entry.publishState).toLowerCase() === 'published'
      ? 'published'
      : 'draft'
  const priceModel: PreforgedOfferPriceModel = shortText(entry.priceModel).toLowerCase() === 'paid' ? 'paid' : 'included'
  const priceCents = priceModel === 'paid' ? readMoneyCents(entry.priceCents) : 0
  const draft = normalizePreforgeDraft(entry.preforgeDraft ?? entry.draft ?? entry)
  const report = normalizePreforgeForgeReport(entry.forgeReport ?? entry.report ?? entry)
  return {
    id,
    kind,
    versionLabel: shortText(entry.versionLabel),
    variantOfOfferId: shortText(entry.variantOfOfferId),
    title: shortText(entry.title, kind === 'reality' ? 'Untitled Reality' : 'Untitled World'),
    summary: shortText(entry.summary),
    worldManifestId: shortText(entry.worldManifestId),
    seedCode: shortText(entry.seedCode, draft?.seed.code),
    vesselCode: shortText(entry.vesselCode),
    starterCode: shortText(entry.starterCode),
    includedPlanId: normalizeCommercePlanId(entry.includedPlanId ?? entry.entitlementTier),
    priceModel,
    priceCents,
    currency: shortText(entry.currency, 'USD').toUpperCase(),
    publishState,
    ...(entry.forgeRecipe || entry.sourceWorld || entry.sourceWorldKind || entry.scale || entry.density || entry.tone || entry.constraints || entry.outputMode
      ? { forgeRecipe: normalizePreforgeRecipe({
          forgeRecipe: entry.forgeRecipe,
          sourceWorld: entry.sourceWorld,
          sourceWorldKind: entry.sourceWorldKind,
          sourceWorldId: entry.sourceWorldId,
          scale: entry.scale,
          density: entry.density,
          tone: entry.tone,
          constraints: entry.constraints,
          outputMode: entry.outputMode,
          aiSelection: entry.aiSelection,
          aiProviderId: entry.aiProviderId,
          aiModel: entry.aiModel,
          providerId: entry.providerId,
          model: entry.model,
        }) ?? undefined }
      : {}),
    ...(draft ? { preforgeDraft: draft } : {}),
    ...(report ? { forgeReport: report } : {}),
    ...(Number.isFinite(entry.updatedAtMs) ? { updatedAtMs: Number(entry.updatedAtMs) } : {}),
    ...(shortText(entry.updatedByUid) ? { updatedByUid: shortText(entry.updatedByUid) } : {}),
    ...(shortText(entry.updatedByEmail) ? { updatedByEmail: shortText(entry.updatedByEmail).toLowerCase() } : {}),
  }
}

export function validatePreforgedOffer(offer: PreforgedOffer): string[] {
  const errors = []
  if (!offer.title) errors.push('title is required')
  if (!offer.worldManifestId) errors.push('worldManifestId is required')
  if (offer.kind === 'reality' && !offer.seedCode && !offer.starterCode) {
    errors.push('reality offers need a seedCode or starterCode')
  }
  if (offer.kind === 'reality' && !offer.vesselCode && !offer.starterCode) {
    errors.push('reality offers need a vesselCode or starterCode')
  }
  if (offer.priceModel === 'paid' && offer.priceCents <= 0) {
    errors.push('paid offers need positive priceCents')
  }
  if (offer.kind === 'reality' && offer.publishState === 'published' && !offer.forgeRecipe) {
    errors.push('published reality offers need a forgeRecipe')
  }
  if (offer.kind === 'reality' && offer.publishState === 'published' && !offer.forgeReport) {
    errors.push('published reality offers need a forgeReport')
  }
  if (offer.variantOfOfferId && offer.variantOfOfferId === offer.id) {
    errors.push('variantOfOfferId cannot match the offer id')
  }
  if (offer.kind === 'reality' && offer.publishState === 'published' && offer.forgeRecipe) {
    const { density, constraints } = offer.forgeRecipe
    const floors = constraints.minimumContentFloors
    const floorChecks: Array<[keyof PreforgeDensityTargets, string]> = [
      ['atlasNodes', 'atlasNodes'],
      ['rooms', 'rooms'],
      ['npcs', 'npcs'],
      ['items', 'items'],
      ['quests', 'quests'],
      ['companions', 'companions'],
      ['clocks', 'clocks'],
      ['events', 'events'],
    ]
    for (const [key, label] of floorChecks) {
      const floor = Number(floors?.[key] ?? 0)
      if (!Number.isFinite(floor) || floor <= 0) continue
      const actual = Number(density?.[key] ?? 0)
      if (actual < floor) {
        errors.push(`forgeRecipe density.${label} must meet minimum floor ${floor}`)
      }
    }
  }
  if (offer.forgeRecipe) {
    const { density, constraints, outputMode } = offer.forgeRecipe
    const sourceWorld = offer.forgeRecipe.sourceWorld
    if (!sourceWorld) errors.push('forgeRecipe.sourceWorld is required')
    if (!density.atlasNodes || !density.rooms || !density.npcs || !density.items || !density.quests) {
      errors.push('forgeRecipe density needs atlasNodes, rooms, npcs, items, and quests')
    }
    if (constraints.tokenBudget <= 0) {
      errors.push('forgeRecipe.constraints.tokenBudget must be positive')
    }
    if (constraints.timeBudgetMs <= 0) {
      errors.push('forgeRecipe.constraints.timeBudgetMs must be positive')
    }
    if (outputMode === 'published-offer' && offer.publishState !== 'published') {
      errors.push('published-offer recipes require publishState=published')
    }
    if (offer.forgeRecipe.aiSelection.providerId !== 'auto' && !offer.forgeRecipe.aiSelection.model.trim()) {
      errors.push('forgeRecipe.aiSelection.model is required when providerId is explicit')
    }
  }
  return errors
}

export function normalizePreforgedOffers(raw: unknown): PreforgedOffer[] {
  const source = Array.isArray(raw) ? raw : []
  const deduped = new Map<string, PreforgedOffer>()
  for (const candidate of source) {
    const normalized = normalizePreforgedOffer(candidate)
    if (!normalized) continue
    deduped.set(normalized.id, normalized)
  }
  return [...deduped.values()].sort((a, b) => a.title.localeCompare(b.title))
}

function readLocalValue<T>(key: string, fallback: T, normalizer: (raw: unknown) => T): T {
  try {
    const raw = localStorage.getItem(key)
    return normalizer(raw ? JSON.parse(raw) : fallback)
  } catch {
    return normalizer(fallback)
  }
}

export function readLocalCommercePlans(): CommercePlan[] {
  return readLocalValue(LOCAL_COMMERCE_PLANS_KEY, DEFAULT_COMMERCE_PLANS, normalizeCommercePlans)
}

export function saveLocalCommercePlans(plans: CommercePlan[]): void {
  try {
    localStorage.setItem(LOCAL_COMMERCE_PLANS_KEY, JSON.stringify(normalizeCommercePlans(plans)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function readLocalPreforgedOffers(): PreforgedOffer[] {
  return readLocalValue(LOCAL_PREFORGED_OFFERS_KEY, [], normalizePreforgedOffers)
}

export function saveLocalPreforgedOffers(offers: PreforgedOffer[]): void {
  try {
    localStorage.setItem(LOCAL_PREFORGED_OFFERS_KEY, JSON.stringify(normalizePreforgedOffers(offers)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function readLocalEntitlementSnapshot(plans: CommercePlan[] = DEFAULT_COMMERCE_PLANS): EntitlementSnapshot {
  return readLocalValue(LOCAL_ENTITLEMENT_SNAPSHOT_KEY, {
    planId: 'free',
    active: false,
    hostedProxyAccess: false,
    managedImages: false,
    creatorConsoleAccess: false,
    preforgedRealityAccess: false,
    managedActionQuota: 0,
    managedImageQuota: 0,
    creatorPublishSlots: 0,
    source: 'default',
  }, raw =>
    normalizeEntitlementSnapshot(raw, plans),
  )
}

export function saveLocalEntitlementSnapshot(snapshot: EntitlementSnapshot, plans: CommercePlan[] = DEFAULT_COMMERCE_PLANS): void {
  try {
    localStorage.setItem(LOCAL_ENTITLEMENT_SNAPSHOT_KEY, JSON.stringify(normalizeEntitlementSnapshot(snapshot, plans)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function readLocalManagedUsageLedger(): ManagedUsageEntry[] {
  return readLocalValue(LOCAL_MANAGED_USAGE_LEDGER_KEY, [], normalizeManagedUsageLedger)
}

export function saveLocalManagedUsageLedger(entries: ManagedUsageEntry[]): void {
  try {
    localStorage.setItem(LOCAL_MANAGED_USAGE_LEDGER_KEY, JSON.stringify(normalizeManagedUsageLedger(entries)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function readLocalMarketplaceArtifactReceipts(): MarketplaceArtifactReceipt[] {
  return readLocalValue(LOCAL_MARKETPLACE_RECEIPTS_KEY, [], normalizeMarketplaceArtifactReceipts)
}

export function saveLocalMarketplaceArtifactReceipts(receipts: MarketplaceArtifactReceipt[]): void {
  try {
    localStorage.setItem(LOCAL_MARKETPLACE_RECEIPTS_KEY, JSON.stringify(normalizeMarketplaceArtifactReceipts(receipts)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function readLocalCommerceTesterGrants(): CommerceTesterGrant[] {
  return readLocalValue(LOCAL_TESTER_GRANTS_KEY, [], normalizeCommerceTesterGrants)
}

export function saveLocalCommerceTesterGrants(grants: CommerceTesterGrant[]): void {
  try {
    localStorage.setItem(LOCAL_TESTER_GRANTS_KEY, JSON.stringify(normalizeCommerceTesterGrants(grants)))
  } catch {
    // Non-fatal local persistence failure.
  }
}
